// src/components/fix-actions/redesign/explain-popover.tsx
//
// "Explain this" popover - anchored to the clicked type label. Content comes
// from ASSET_TYPE_INFO keyed by the real ArtifactType.

import { useEffect, useState } from "react";
import { ASSET_TYPE_INFO, typeColor, Ic } from "./shared";

export interface ExplainAnchor {
  type: string;
  x: number;
  y: number;
}

export function ExplainPopover({
  anchor,
  onClose,
}: {
  anchor: ExplainAnchor | null;
  onClose: () => void;
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(!!anchor);
  }, [anchor]);

  if (!anchor) return null;
  const info = ASSET_TYPE_INFO[anchor.type];
  const col = typeColor(anchor.type);
  const left = Math.min(anchor.x, (typeof window !== "undefined" ? window.innerWidth : 1280) - 360);
  const top = anchor.y;

  return (
    <div>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 69 }} />
      <div
        className="ai-fade-in"
        style={{
          position: "fixed",
          zIndex: 70,
          width: 340,
          maxWidth: "88vw",
          left,
          top,
          background: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          boxShadow: "var(--elevation-lg)",
          padding: 18,
          opacity: mounted ? 1 : 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                width: 24,
                height: 24,
                borderRadius: 7,
                background: "var(--spectral-violet-10)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--spectral-violet)",
                flexShrink: 0,
              }}
            >
              <Ic paths={["M5 12h14M12 5v14"]} size={14} sw={1.8} />
            </span>
            <span style={{ fontSize: 14, fontWeight: 600, color: col }}>{anchor.type}</span>
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted-foreground)", padding: 0 }}
          >
            <Ic paths={["M18 6 6 18M6 6l12 12"]} size={16} sw={2} />
          </button>
        </div>

        {info ? (
          <>
            <p style={{ fontSize: 13, color: "var(--muted-foreground)", lineHeight: 1.6, margin: "0 0 12px" }}>{info.blurb}</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 7, marginBottom: 14 }}>
              {info.bullets.map((b, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--foreground)" }}>
                  <span style={{ width: 4, height: 4, borderRadius: 9999, background: "var(--muted-foreground)", flexShrink: 0 }} />
                  {b}
                </div>
              ))}
            </div>
            <div style={{ background: "var(--spectral-violet-5, var(--muted))", border: "1px solid var(--spectral-violet-20)", borderRadius: 9, padding: "11px 13px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, color: "var(--spectral-violet)" }}>
                <Ic paths={["M5 12h14M12 5v14"]} size={13} sw={1.9} />
                <span style={{ fontSize: 12, fontWeight: 600 }}>Pro tip</span>
              </div>
              <p style={{ fontSize: 12.5, color: "var(--foreground)", lineHeight: 1.5, margin: 0 }}>{info.tip}</p>
            </div>
          </>
        ) : (
          <p style={{ fontSize: 13, color: "var(--muted-foreground)", lineHeight: 1.6, margin: 0 }}>
            A structured asset that helps AI models describe and recommend you accurately.
          </p>
        )}
      </div>
    </div>
  );
}
